import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { JsonObjectFactoryService } from '../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { EfileEditKeyTableDataService } from './efile-edit-key-table-data.service';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import * as _ from 'lodash-es';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  sanitizeActionParams,
} from '../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-edit-key-table-data',
    templateUrl: './efile-edit-key-table-data.component.html',
    styleUrls: ['./efile-edit-key-table-data.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EfileEditKeyTableDataComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subs = new Subscription();
  // Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;

  // Filter params input for dynamic data
  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };
  rowData: any = {};

  // Component properties
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  mode: string = 'create';
  title: string = 'Create Entry';
  minimise: boolean = false;
  crudLoading: boolean = false;
  saving: boolean = false;
  deleting: boolean = false;
  selectedTaxYear: string = '';
  showSubmissionIdRequiredError: boolean = false;
  showKeyIdRequiredError: boolean = false;
  showEntityRequiredError: boolean = false;
  showEntitySubRequiredError: boolean = false;
  showEntityTypeIdRequiredError: boolean = false;
  showOracleUserIdRequiredError: boolean = false;

  // Form fields
  submissions: any[] = [];
  selectedSubId: any = null;
  submissionId: string = '';
  keyId: string = '';
  entity: string = '';
  entitySub: string = '';
  parentId: string = '';
  oracleUserId: string = '';
  entityTypeId: string = '';

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private efileEditKeyTableDataService: EfileEditKeyTableDataService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  // Gate function to prevent duplicate initialization
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    // Check if ALL required data is present before initializing
    if (!this.globalService || !this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.mode = this.rowData && Object.keys(this.rowData).length > 0 ? 'edit' : 'create';
      this.title = this.mode === 'create' ? 'Create Entry' : 'Edit Entry';
      this.selectedTaxYear = this.filterParams.tax_year || '';
      this.loadSubmissions();
    } catch (e) {
      console.error('Error in component init:', e);
      this.cdr.detectChanges();
    }
  }

  loadSubmissions(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    const params = {
      'action_code': 'pfnojm',
      'sso_id': this.USER_SETTINGS?.user?.sso_id || '',
      'tax_year': this.selectedTaxYear
    };

    this.selectedSubId = null;
    this.submissions = [];

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess !== "1") {
          this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error loading submissions', 'gtw-snackbar--red');
        } else {
          this.submissions = data.jsonObject || [];
          
          if (this.mode === 'edit' && this.rowData) {
            const selectedElement = this.getSummaryObj(this.rowData.SUBMISSION_ID);
            this.submissionId = this.rowData.SUBMISSION_ID;
            this.selectedSubId = this.rowData.SUBMISSION_ID;
            this.keyId = this.rowData.KEY_ID || '';
            this.entity = this.rowData.ENTITY || '';
            this.entitySub = this.rowData.ENTITY_SUB || '';
            this.parentId = this.rowData.PARENT_ID || '';
            this.entityTypeId = this.rowData.ENTITY_TYPE_ID || '';
            this.oracleUserId = this.rowData.ORACLE_USER_ID || '';
          }
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading submissions:', error);
        this.gtwSnackbarService.openSnackBar('Something went wrong while loading key table!', 'gtw-snackbar--red');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  getSummaryObj(summaryValue: string): any {
    return this.submissions.find((ele) => {
      const cbcSummaryValue = ele.SUBMISSION_ID;
      return cbcSummaryValue === summaryValue;
    });
  }

  prepareActionCall(mode: string): any {
    const CREATE_ACTION_CODE = 'A';
    const UPDATE_ACTION_CODE = 'E';
    const DELETE_ACTION_CODE = 'D';
    let actionCode;
    if (mode === 'create') {
      actionCode = CREATE_ACTION_CODE;
    } else if (mode === 'edit') {
      actionCode = UPDATE_ACTION_CODE;
    } else {
      actionCode = DELETE_ACTION_CODE;
    }
    const url = this.GENERAL_CONFIG.base_url + '/saveJsonObject?action_code=wkn3u3';

    const clobSettings = [{
      tax_year: this.filterParams.tax_year || '',
      scenario: this.filterParams.scenario || '',
      jcd_key: this.filterParams.jcd_key || ''
    }];

    if (this.mode === 'edit') {
      this.submissionId = this.rowData.SUBMISSION_ID;
    }

    // In edit mode: use rowData.SUBMISSION_ID; in create mode: use selectedSubId.SUBMISSION_ID (object property)
    const submissionIdValue = this.submissionId ? this.rowData.SUBMISSION_ID : (this.selectedSubId?.SUBMISSION_ID || this.selectedSubId);

    // Build clobData
    const clobData: any = {
      'submission_id': submissionIdValue,
      'parent_id': this.parentId || '',
      'created_by': this.USER_SETTINGS?.user?.sso_id || ''
    };

    if (this.mode === 'edit') {
      // In edit mode: only include properties that have values
      // When a value is removed/empty, that particular property should not be included
      if (this.keyId !== null && this.keyId !== undefined && this.keyId !== '') {
        clobData['key_id'] = this.keyId;
      }

      if (this.entity !== null && this.entity !== undefined && this.entity !== '') {
        clobData['entity'] = this.entity;
      }

      if (this.entitySub !== null && this.entitySub !== undefined && this.entitySub !== '') {
        clobData['entity_sub'] = this.entitySub;
      }

      if (this.entityTypeId !== null && this.entityTypeId !== undefined && this.entityTypeId !== '') {
        clobData['entity_type_id'] = this.entityTypeId;
      }

      if (this.oracleUserId !== null && this.oracleUserId !== undefined && this.oracleUserId !== '') {
        clobData['oracle_user_id'] = this.oracleUserId;
      }
    } else {
      // In create mode: include all fields (even if empty)
      clobData['key_id'] = this.keyId || '';
      clobData['entity'] = this.entity || '';
      clobData['entity_sub'] = this.entitySub || '';
      clobData['parent_id'] = this.parentId || '';
      clobData['entity_type_id'] = this.entityTypeId || '';
      clobData['oracle_user_id'] = this.oracleUserId || '';
    }

    let params = _.merge({}, this.globalService || {}, this.filterParams);
    params = _.extend({ 'clob_data': JSON.stringify(clobData) }, params);
    params = _.extend({ 'clob_settings': JSON.stringify(clobSettings) }, params);
    params.sso_id = this.USER_SETTINGS?.user?.sso_id || '';
    params.mode = actionCode;
    params.scenarioCode = this.filterParams.scenarioCode || this.globalService.scenarioCode || 'RAF';
    params.is_issue_key_enabled = this.filterParams.is_issue_key_enabled || this.globalService.is_issue_key_enabled || 'N';

    // Build checkLockedbj array with scenario_key
    const scenarioKey = parseInt(this.filterParams?.scenario || this.globalService?.scenario || '74');
    const checkLockedbj = [{
      scenario_key: scenarioKey
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    return { url: url, params: sanitizeActionParams(params) };
  }

  save(): void {
    console.log("inside save method of key table");
    
    // Prevent double-click
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--red');
      return;
    }

    // Validation - check Submission Id in create mode
    if (this.mode !== 'edit' && !this.selectedSubId) {
      this.showSubmissionIdRequiredError = true;
      this.gtwSnackbarService.openSnackBar('Submission Id is required', 'gtw-snackbar--red');
      this.cdr.detectChanges();
      return;
    }

    // Validation - check all fields have values (except parent_id)
    let hasValidationError = false;
    
    const keyIdStr = this.keyId ? String(this.keyId).trim() : '';
    if (!keyIdStr) {
      this.showKeyIdRequiredError = true;
      hasValidationError = true;
    } else {
      this.showKeyIdRequiredError = false;
    }

    const entityStr = this.entity ? String(this.entity).trim() : '';
    if (!entityStr) {
      this.showEntityRequiredError = true;
      hasValidationError = true;
    } else {
      this.showEntityRequiredError = false;
    }

    const entitySubStr = this.entitySub ? String(this.entitySub).trim() : '';
    if (!entitySubStr) {
      this.showEntitySubRequiredError = true;
      hasValidationError = true;
    } else {
      this.showEntitySubRequiredError = false;
    }

    const entityTypeIdStr = this.entityTypeId ? String(this.entityTypeId).trim() : '';
    if (!entityTypeIdStr) {
      this.showEntityTypeIdRequiredError = true;
      hasValidationError = true;
    } else {
      this.showEntityTypeIdRequiredError = false;
    }

    const oracleUserIdStr = this.oracleUserId ? String(this.oracleUserId).trim() : '';
    if (!oracleUserIdStr) {
      this.showOracleUserIdRequiredError = true;
      hasValidationError = true;
    } else {
      this.showOracleUserIdRequiredError = false;
    }

    if (hasValidationError) {
      this.gtwSnackbarService.openSnackBar('Please fill all required fields', 'gtw-snackbar--red');
      this.cdr.detectChanges();
      return;
    }

    this.showSubmissionIdRequiredError = false;
    const urlParamsObj = this.prepareActionCall(this.mode);
    this.crudLoading = true;
    this.saving = true;
    this.cdr.detectChanges();

    this.efileEditKeyTableDataService.saveKeyTableData(
      urlParamsObj.url,
      urlParamsObj.params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        this.saving = false;

        if (data.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
        } else {
          this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error saving data', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error saving data:', error);
        this.crudLoading = false;
        this.saving = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong while saving data, please try again!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  delete(): void {
    console.log("inside delete of key table data");

    if (!this.keyId) {
      return;
    }

    // Prevent double-click
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we delete this request', 'gtw-snackbar--red');
      return;
    }

    const urlParamsObj = this.prepareActionCall('delete');
    this.crudLoading = true;
    this.deleting = true;
    this.cdr.detectChanges();

    this.efileEditKeyTableDataService.deleteKeyTableData(
      urlParamsObj.url,
      urlParamsObj.params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        this.deleting = false;

        if (data.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data deleted successfully.', 'gtw-snackbar--green');
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
        } else {
          this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error deleting data', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error deleting data:', error);
        this.crudLoading = false;
        this.deleting = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong while deleting the data, please try again!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
    // NO $uibModalInstance.dismiss('cancel') or this.dialog.closeAll()
  }
}
